import {
  Box,
  Grid,
  GridItem,
  FormControl,
  FormErrorMessage,
  Input,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from '@chakra-ui/react';
import Image from 'next/image';

import { Formik, Form, Field, FormikErrors, FormikTouched } from 'formik';
import { useRouter } from 'next/router';
import { useState } from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '../LightLP/Button';
import ChevronDown from '../Icons/ChevronDown';

import { gql, useMutation } from '@apollo/client';

const TalentSchema = {
  firstName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('First name is required'),
  lastName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  profileUrl: Yup.string().url('Invalid url').required('LinkedIn or personal website is required'),
  fileUrl: Yup.string().url('Invalid url'),
  country: Yup.string().required('Country is required'),
  skills: Yup.array()
    .min(1, 'At least 1 skill is required')
    .required('Tech stack expertise is required'),
  subscribe: Yup.boolean(),
};

const EXTERNAL_CANDIDATE_MUTATION = gql`
  mutation InsertExternalCandidate($candidate_data: ExternalCandidateInput!) {
    insertExternalCandidate(candidate: $candidate_data) {
      id
    }
  }
`;

interface FormProps {
  title: string;
  text?: string;
  thankYouUrl: string;
  countryOptions?: {
    name: string;
    image: string;
  }[];
  skillsOptions?: string[];
  theme: {
    green100?: string;
    green200?: string;
    green300?: string;
    blue100?: string;
    blue200?: string;
    blue300?: string;
    yellow100?: string;
    yellow200?: string;
    yellow300?: string;
    red100?: string;
  };
}

export default function ExternalCandidateLightLP({
  title,
  text,
  thankYouUrl,
  countryOptions = [],
  skillsOptions = [],
  theme,
}: FormProps) {
  const FormSchema = Yup.object().shape(TalentSchema);
  const router = useRouter();
  const [submitError, setSubmitError] = useState('');
  const [radioValue, setRadioValue] = useState('Country of residence');

  const [submitCandidate, { data, loading, error }] = useMutation(EXTERNAL_CANDIDATE_MUTATION, {
    context: { clientName: 'externalCandidate' },
  });

  // if (loading) return 'Submitting...';
  // if (error) return `Submission error! ${error.message}`;

  const formSubmissionUrl = () => {
    if (window && window.location.href.includes('localhost')) {
      return 'localhost.terminal.io';
    } else {
      return window.location.href ? window.location.href.split('?')[0].toLowerCase() : null;
    }
  };

  const referringUrl = () => {
    if (document && document.referrer.includes('localhost')) {
      return 'localhost.terminal.io';
    } else {
      return document && document.referrer ? document.referrer.toLowerCase() : null;
    }
  };

  const FormInput = ({ type, placeholder, ...field }: { type: string; placeholder: string }) => {
    return (
      <Input
        type={type}
        placeholder={placeholder}
        borderColor="grey.400"
        borderRadius="0.25rem"
        bgColor="#fff"
        _placeholder={{ color: 'grey.500' }}
        focusBorderColor={theme.green200}
        {...field}
      />
    );
  };

  const handleCountry = (country: string) => {
    return country === 'Poland - Ukrainian refugee' ? 'Poland' : country;
  };

  return (
    <Box
      bgColor="white"
      px={{ base: '4', sm: '12' }}
      py={{ base: '6', sm: '12' }}
      boxShadow="0px 8px 16px rgba(0, 0, 0, 0.08)"
      borderRadius="1rem"
      maxW="29.5rem"
      mx="auto"
    >
      {title && (
        <Box fontSize="2rem" line-height="2.25rem" fontWeight="bold" color="grey.900" mb="6">
          {title}
        </Box>
      )}
      {text && (
        <Box line-height="1.25rem" color="grey.700" mb="1">
          {text}
        </Box>
      )}
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          profileUrl: '',
          fileUrl: '',
          country: '',
          skills: [],
          subscribe: false,
        }}
        validationSchema={FormSchema}
        onSubmit={async (values, actions) => {
          // form data for external candidate
          const candidateVariable = {
            candidate_data: {
              email: values.email.toLowerCase(),
              firstName: values.firstName,
              lastName: values.lastName,
              linkedinOrPersonalUrl: values.profileUrl.toLowerCase(),
              cvOrResumeUrl: values.fileUrl ? values.fileUrl.toLowerCase() : null,
              source: 'LANDING_PAGE',
              skillsArray: values.skills,
              countryOfResidence: handleCountry(values.country),
              agencyContactEmail: null,
              yearsOfExperience: null,
              utmCampaign: router.query.utm_campaign ?? null,
              utmMedium: router.query.utm_medium ?? null,
              utmSource: router.query.utm_source ?? null,
              utmContent: router.query.utm_content ?? null,
              utmTerm: router.query.utm_term ?? null,
              cid: router.query.cid ?? null,
              formSubmissionUrl: formSubmissionUrl(),
              referringUrl: referringUrl(),
            },
          };

          // form data for Zapier Consent and Tracking
          const zapFormConsent = {
            email: candidateVariable.candidate_data.email,
            first_name: candidateVariable.candidate_data.firstName,
            last_name: candidateVariable.candidate_data.lastName,
            profileUrl: candidateVariable.candidate_data.linkedinOrPersonalUrl,
            expLevel: null,
            country: candidateVariable.candidate_data.countryOfResidence,
            techStack: candidateVariable.candidate_data.skillsArray,
            utm_campaign: candidateVariable.candidate_data.utmCampaign,
            utm_content: candidateVariable.candidate_data.utmContent,
            utm_term: candidateVariable.candidate_data.utmTerm,
            medium: candidateVariable.candidate_data.utmMedium,
            source: candidateVariable.candidate_data.utmSource,
            cid: candidateVariable.candidate_data.cid,
            subscribe: values.subscribe,
            form_completion_url: candidateVariable.candidate_data.formSubmissionUrl,
          };

          // email consent and tracking
          const zapRequest = fetch('/api/engineers-guides', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(zapFormConsent),
          });

          const externalCandidateRequest = submitCandidate({
            variables: { candidate_data: candidateVariable.candidate_data },
          });

          Promise.allSettled([externalCandidateRequest, zapRequest])
            .then((res) => {
              actions.setSubmitting(true);
              if (res[0].status === 'fulfilled') {
                (window as any).dataLayer.push({
                  event: 'candidate_submission',
                  candidateId: res[0].value.data.insertExternalCandidate.id,
                  page: router.pathname,
                });
              }
              router.push(thankYouUrl);
            })
            .catch((error) => {
              actions.setSubmitting(false);
              setSubmitError('Form submission failed. Please, try again later.');
            });
        }}
      >
        {({
          errors,
          touched,
          isSubmitting,
        }: {
          errors: FormikErrors<{
            firstName: string;
            lastName: string;
            email: string;
            profileUrl: string;
            fileUrl: string;
            country: string;
            skills: string[];
            subscribe: boolean;
          }>;
          touched: FormikTouched<{
            firstName: string;
            lastName: string;
            email: string;
            profileUrl: string;
            fileUrl: string;
            country: string;
            skills: string[];
            subscribe: boolean;
          }>;
          isSubmitting: boolean;
        }) => (
          <Form>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="1rem" mt="1rem">
              <GridItem>
                <Field name="firstName">
                  {({ field }) => (
                    <FormControl isInvalid={errors.firstName && touched.firstName} id="firstName">
                      <FormInput type="text" placeholder="First name" {...field} />
                      <FormErrorMessage mt="px">{errors.firstName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </GridItem>
              <GridItem>
                <Field name="lastName">
                  {({ field }) => (
                    <FormControl isInvalid={errors.lastName && touched.lastName} id="lastName">
                      <FormInput type="text" placeholder="Last name" {...field} />
                      <FormErrorMessage mt="px">{errors.lastName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </GridItem>
              <GridItem gridArea={{ md: '2 / 1 / 3 / 3' }}>
                <Field name="email">
                  {({ field }) => (
                    <FormControl isInvalid={errors.email && touched.email} id="email">
                      <FormInput type="email" placeholder="Email" {...field} />
                      <FormErrorMessage mt="px">{errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <FormLabel fontWeight="normal" color="grey.500" mt="1rem">
                  Tech Stack
                </FormLabel>
                <FormControl isInvalid={errors.skills && touched.skills}>
                  <FormErrorMessage mt="px">{errors.skills}</FormErrorMessage>
                </FormControl>
                {skillsOptions.map((option) => (
                  <FormControl
                    key={option}
                    id={option}
                    mt="0.5rem"
                    mr="0.5rem"
                    display="inline-block"
                    w="auto"
                  >
                    <Field
                      name="skills"
                      as={Checkbox}
                      value={option}
                      _hover={{
                        backgroundColor: 'grey.200',
                      }}
                      _checked={{
                        backgroundColor: 'grey.900',
                        borderColor: 'grey.900',
                      }}
                      bgColor="white"
                      border="0.0625rem solid #637A83"
                      borderRadius="2rem"
                      ml="0"
                      p="0.625rem 1rem"
                      transition="all 0.3s ease"
                      sx={{
                        '> span:first-of-type': {
                          display: 'none',
                        },
                        span: {
                          ml: 0,
                        },
                      }}
                      role="group"
                    >
                      <Box
                        color="grey.600"
                        lineHeight="1.25"
                        _groupChecked={{
                          color: 'white',
                        }}
                      >
                        {option}
                      </Box>
                    </Field>
                  </FormControl>
                ))}
                <Field name="profileUrl">
                  {({ field }) => (
                    <FormControl
                      isInvalid={errors.profileUrl && touched.profileUrl}
                      id="profileUrl"
                      mt="1rem"
                    >
                      <FormInput
                        type="text"
                        placeholder="LinkedIn or personal website"
                        {...field}
                      />
                      <FormErrorMessage mt="px">{errors.profileUrl}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="fileUrl">
                  {({ field }) => (
                    <FormControl
                      isInvalid={errors.fileUrl && touched.fileUrl}
                      id="fileUrl"
                      mt="1rem"
                    >
                      <FormInput type="text" placeholder="CV or resume file URL" {...field} />
                      <FormErrorMessage mt="px">{errors.fileUrl}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Box mt="1rem">
                  <Menu flip={false} gutter={4} matchWidth>
                    {({ isOpen }) => (
                      <>
                        <MenuButton
                          as={Button}
                          rightIcon={
                            <ChevronDown
                              transform={{
                                base: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                              }}
                              transition="transform .3s ease-in-out"
                              color="grey.900"
                            />
                          }
                          bgColor="white"
                          borderRadius="0.25rem"
                          borderWidth="0.0625rem"
                          boxShadow={errors.country && touched.country && '0 0 0 1px #e53e3e'}
                          borderColor={errors.country && touched.country ? 'red' : 'grey.400'}
                          color={radioValue === 'Country of residence' && 'grey.500'}
                          fontWeight="normal"
                          w="100%"
                          textAlign="left"
                          _hover={{ bg: 'grey.200' }}
                          _active={{ bg: 'grey.200' }}
                          _expanded={{ bg: 'white' }}
                        >
                          {radioValue}
                        </MenuButton>
                        <RadioGroup
                          as={MenuList}
                          onChange={setRadioValue}
                          value={radioValue}
                          boxShadow="0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)"
                          borderRadius="0.25rem"
                          minWidth="100%"
                          py="1rem"
                          px="1rem"
                        >
                          {countryOptions.map((option) => (
                            <MenuItem
                              as="div"
                              key={option.name}
                              p="0.625rem"
                              _hover={{
                                bgColor: 'grey.200',
                              }}
                            >
                              <Flex
                                alignItems="center"
                                w="100%"
                                sx={{
                                  '.chakra-radio__control': {
                                    display: 'none',
                                  },
                                }}
                              >
                                <Box position="relative" w="6" h="6">
                                  <Image
                                    src={option.image}
                                    alt={option.name}
                                    style={{ objectFit: 'contain' }}
                                    fill
                                  />
                                </Box>
                                <Field name="country" as={Radio} value={option.name} w="100%">
                                  {option.name}
                                </Field>
                              </Flex>
                            </MenuItem>
                          ))}
                        </RadioGroup>
                      </>
                    )}
                  </Menu>
                  <FormControl isInvalid={errors.country && touched.country}>
                    <FormErrorMessage mt="px">{errors.country}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Field name="subscribe">
                  {({ field }) => (
                    <FormControl mt="1rem" id="subscribe">
                      <Checkbox colorScheme="grey" {...field}>
                        <Box fontSize="sm" color="grey.500" lineHeight="1.25">
                          I would like to receive future communications from Terminal
                        </Box>
                      </Checkbox>
                    </FormControl>
                  )}
                </Field>
                <PrimaryButton
                  mt="6"
                  type="submit"
                  theme={theme}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  Join Terminal
                </PrimaryButton>
              </GridItem>
            </Grid>
            {submitError && (
              <Box fontSize="xs" mt="1" color="red">
                {submitError}
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
}
